import type { Question } from '@zg/shared/property';
import { TextField } from '../form/TextField';
import { CurrencyField } from '../form/CurrencyField';
import { DateField } from '../form/DateField';
import { Select } from '../form/Select';
import { US_STATES } from '../../lib/usStates';
import { CheckboxGroup, RadioGroup } from './FormControls';

export interface QuestionRendererProps {
  question: Question;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
}

/**
 * Dispatches a canonical Question to the appropriate form primitive.
 * Every question type declared in shared/property/types.ts is handled here.
 */
export function QuestionRenderer({
  question,
  value,
  onChange,
  error,
}: QuestionRendererProps) {
  const { type, label, placeholder, helpText, options, required } = question;
  const labelWithRequired = required ? `${label} *` : label;
  // The TextField floating-label occupies the placeholder slot when empty,
  // so passing HTML `placeholder` would double-render. Surface the question's
  // `placeholder` (format hints like "00-0000000") as `hint` instead, below
  // the input, where it doesn't collide.
  const hint = helpText ?? (placeholder && placeholder.length < 40 ? placeholder : undefined);

  switch (type) {
    case 'text':
      return (
        <TextField
          label={labelWithRequired}
          hint={hint}
          error={error}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'textarea':
      return (
        <TextareaField
          label={labelWithRequired}
          placeholder={placeholder}
          hint={helpText}
          error={error}
          value={(value as string) ?? ''}
          onChange={(v) => onChange(v)}
        />
      );

    case 'currency':
      return (
        <CurrencyField
          label={labelWithRequired}
          hint={hint}
          error={error}
          value={(value as number | undefined) ?? undefined}
          onValueChange={(n) => onChange(n)}
        />
      );

    case 'number':
    case 'percent':
    case 'year':
      return (
        <TextField
          label={labelWithRequired}
          hint={hint}
          error={error}
          inputMode="numeric"
          numeric
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => {
            const s = e.target.value;
            if (s === '') {
              onChange(undefined);
              return;
            }
            const n = Number(s);
            onChange(Number.isFinite(n) ? n : s);
          }}
        />
      );

    case 'date':
      return (
        <DateField
          label={labelWithRequired}
          hint={hint}
          error={error}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'email':
      return (
        <TextField
          label={labelWithRequired}
          hint={hint}
          error={error}
          type="email"
          autoComplete="email"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'phone':
      return (
        <TextField
          label={labelWithRequired}
          hint={hint ?? '(000) 000-0000'}
          error={error}
          type="tel"
          autoComplete="tel"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'state':
      return (
        <Select
          label={labelWithRequired}
          hint={helpText}
          error={error}
          placeholder="Select a state"
          options={US_STATES}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'select':
      return (
        <Select
          label={labelWithRequired}
          hint={helpText}
          error={error}
          placeholder={placeholder ?? 'Select an option'}
          options={(options ?? []).map((o) => ({
            value: o.value,
            label: o.label,
          }))}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'radio':
    case 'yesno':
      return (
        <RadioGroup
          name={question.id}
          label={labelWithRequired}
          hint={helpText}
          error={error}
          options={(options ?? []).map((o) => ({ value: o.value, label: o.label }))}
          value={(value as string) ?? undefined}
          onChange={(v) => onChange(v)}
          layout={type === 'yesno' ? 'grid-2' : 'stack'}
        />
      );

    case 'multiselect':
      return (
        <CheckboxGroup
          label={labelWithRequired}
          hint={helpText}
          error={error}
          options={(options ?? []).map((o) => ({ value: o.value, label: o.label }))}
          values={Array.isArray(value) ? (value as string[]) : []}
          onChange={(v) => onChange(v)}
          layout="grid-2"
        />
      );

    case 'checkbox':
      // Single yes/no checkbox; render as small styled control.
      return (
        <label className="flex items-start gap-3 rounded-md border border-hairline bg-panel px-3 py-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="mt-1 h-4 w-4 border-hairline text-blue-deep focus:ring-blue/30 rounded"
          />
          <span className="flex flex-col">
            <span className="font-display text-[0.9375rem] text-ink leading-tight">
              {labelWithRequired}
            </span>
            {helpText ? (
              <span className="font-display text-[0.8125rem] text-ink-subtle mt-0.5">
                {helpText}
              </span>
            ) : null}
            {error ? (
              <span className="font-display text-[0.8125rem] text-[#b33a3a] mt-1">
                {error}
              </span>
            ) : null}
          </span>
        </label>
      );

    default: {
      // Exhaustiveness guard — TypeScript should never let us land here.
      const _never: never = type;
      return (
        <div className="font-mono text-[0.75rem] text-[#b33a3a]">
          &gt; UNKNOWN_TYPE · {String(_never)}
        </div>
      );
    }
  }
}

function TextareaField({
  label,
  placeholder,
  hint,
  error,
  value,
  onChange,
}: {
  label: string;
  placeholder?: string;
  hint?: string;
  error?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="block font-mono text-[0.625rem] uppercase tracking-[0.125em] text-ink-subtle">
        {label}
      </label>
      <textarea
        rows={4}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        className={`w-full bg-panel border rounded-md px-3 py-2 text-[0.9375rem] font-display resize-y focus:outline-none focus:border-blue focus:ring-2 focus:ring-blue/30 ${
          error ? 'border-[#b33a3a]' : 'border-hairline hover:border-ink/30'
        }`}
      />
      {hint ? (
        <p className="font-display text-[0.8125rem] text-ink-subtle">{hint}</p>
      ) : null}
      {error ? (
        <p className="font-display text-[0.8125rem] text-[#b33a3a]">{error}</p>
      ) : null}
    </div>
  );
}
